import React from 'react';
import { Star, ExternalLink, Play, Tag } from 'lucide-react';
import { Game } from '../types';

interface GameCardProps {
  game: Game;
  onOpenDetails?: (game: Game) => void;
}

export const GameCard: React.FC<GameCardProps> = ({ game, onOpenDetails }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Released':
        return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'Early Access':
        return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'In Development':
        return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
      default:
        return 'bg-slate-700/50 text-slate-300 border-slate-600/30';
    }
  };

  return (
    <div className="group relative bg-slate-900/60 rounded-2xl border border-slate-800/80 hover:border-cyan-500/40 overflow-hidden flex flex-col transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/10 hover:-translate-y-1">
      {/* Thumbnail Banner */}
      <div className="relative aspect-video w-full overflow-hidden bg-slate-950">
        <img
          src={game.thumbnail_url}
          alt={game.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent" />

        {/* Top Badges */}
        <div className="absolute top-3 left-3 right-3 flex items-center justify-between pointer-events-none">
          <span
            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border backdrop-blur-md ${getStatusColor(
              game.status
            )}`}
          >
            {game.status}
          </span>
          <div className="flex items-center gap-1 bg-slate-950/80 backdrop-blur-md px-2.5 py-1 rounded-full border border-slate-800 text-amber-400 text-xs font-semibold">
            <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
            <span>{game.rating.toFixed(1)}</span>
          </div>
        </div>

        {/* Genre Pill */}
        <div className="absolute bottom-3 left-3">
          <span className="text-xs font-medium px-2.5 py-0.5 rounded-md bg-cyan-950/80 text-cyan-300 border border-cyan-800/60 backdrop-blur-sm">
            {game.genre}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
        <div>
          <h3 className="text-lg font-bold text-white group-hover:text-cyan-300 transition-colors">
            {game.title}
          </h3>
          <p className="text-slate-400 text-xs line-clamp-2 mt-1.5 leading-relaxed">
            {game.description}
          </p>
        </div>

        {/* Tags */}
        {game.tags && game.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {game.tags.slice(0, 3).map((tag, idx) => (
              <span
                key={idx}
                className="text-[10px] px-2 py-0.5 rounded bg-slate-800/80 text-slate-400 border border-slate-700/50 flex items-center gap-1"
              >
                <Tag className="w-2.5 h-2.5 text-slate-500" />
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        <div className="pt-2 flex items-center gap-2">
          {game.play_url ? (
            <a
              href={game.play_url}
              target="_blank"
              rel="noreferrer"
              className="flex-1 py-2 px-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-slate-950 font-bold text-xs flex items-center justify-center gap-1.5 transition shadow-sm"
            >
              <Play className="w-3.5 h-3.5 fill-current" />
              <span>Play Now</span>
            </a>
          ) : null}

          <button
            onClick={() => onOpenDetails && onOpenDetails(game)}
            className="flex-1 py-2 px-3 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-200 text-xs font-semibold flex items-center justify-center gap-1.5 border border-slate-700/60 transition"
          >
            <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
            <span>Details</span>
          </button>
        </div>
      </div>
    </div>
  );
};
